import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import privateApi from "../../apis/privateApi";
import Spinner from "../../components/Spinner/Spinner";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import "./AdminUser.css";

const AdminUser = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const userId = location.state?.userId;

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const [resendOpen, setResendOpen] = useState(false);
    const [resendToken, setResendToken] = useState("");
    const [resendUserId, setResendUserId] = useState(null);

    const [forceLogoutOpen, setForceLogoutOpen] = useState(false);
    const [forceLogoutToken, setForceLogoutToken] = useState("");
    const [forceLogoutUserId, setForceLogoutUserId] = useState(null);

    const genToken = () => Math.random().toString(36).substring(2, 8).toUpperCase();

    useEffect(() => {
        (async () => {
            try {
                const res = await privateApi.get(`/api/users/${userId}`);
                setUser(res.data.data);
            } catch { /* handle silently */ }
            finally { setLoading(false); }
        })();
    }, [userId]);

    const handleConfirmResend = async (input) => {
        if (input !== resendToken) { alert("Incorrect confirmation code."); setResendOpen(false); return; }
        try {
            await privateApi.post(`/api/users/${resendUserId}/resend-registration`);
            alert("Registration email resent successfully!");
        } catch (err) {
            alert(err.response?.data?.message || "Failed to resend registration email.");
        } finally { setResendOpen(false); setResendUserId(null); }
    };

    const handleConfirmForceLogout = async (input) => {
        if (input !== forceLogoutToken) { alert("Incorrect confirmation code."); setForceLogoutOpen(false); return; }
        try {
            await privateApi.post(`/api/users/${forceLogoutUserId}/force-logout`);
            alert("User has been logged out successfully!");
        } catch (err) {
            alert(err.response?.data?.message || "Failed to log out user.");
        } finally { setForceLogoutOpen(false); setForceLogoutUserId(null); }
    };

    if (loading) return <Spinner loadingInfo="Loading user details…" />;
    if (!user) return <div className="adm-page"><p style={{ color: "var(--muted)" }}>No user data found.</p></div>;

    /* Initials for avatar */
    const initials = (user.name || user.email || "U")
        .split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

    const Field = ({ label, children }) => (
        <div className="adm-field">
            <span className="adm-field-label">{label}</span>
            <span className="adm-field-value">{children}</span>
        </div>
    );

    const renderList = (title, items) => (
        <div className="adm-field">
            <span className="adm-field-label">{title}</span>
            <div className="adm-field-value">
                {items && items.length > 0 ? (
                    <ul className="adm-access-list">
                        {items.map((item, i) => <li key={i}>{item}</li>)}
                    </ul>
                ) : <span style={{ color: "var(--muted-light)" }}>—</span>}
            </div>
        </div>
    );

    return (
        <div className="adm-page">
            <div className="adm-inner">
                <h1 className="adm-page-title">User Details</h1>

                <div className="adm-detail-card">
                    {/* Card Header */}
                    <div className="adm-detail-header">
                        <div className="adm-detail-avatar">{initials}</div>
                        <div className="adm-detail-name-wrap">
                            <div className="adm-detail-name">{user.name || "—"}</div>
                            <div className="adm-detail-email">{user.email}</div>
                        </div>
                        <div className="adm-badge-row">
                            <span className={`adm-badge ${user.isActive ? "active" : "inactive"}`}>
                                <span className="adm-badge-dot" />
                                {user.isActive ? "Active" : "Inactive"}
                            </span>
                            <span className={`adm-badge ${user.emailVerified ? "verified" : "unverified"}`}>
                                {user.emailVerified ? "✓ Verified" : "⚠ Unverified"}
                            </span>
                        </div>
                    </div>

                    {/* Fields */}
                    <div className="adm-fields">
                        <Field label="Role">{user.roleId?.name || "—"}</Field>

                        <div className="adm-field">
                            <span className="adm-field-label">Permissions</span>
                            <div className="adm-field-value">
                                {user.roleId?.permissions?.length > 0 ? (
                                    <ul className="adm-pill-list">
                                        {user.roleId.permissions.map((p, i) => (
                                            <li key={i} className="adm-pill">
                                                {p.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
                                            </li>
                                        ))}
                                    </ul>
                                ) : <span style={{ color: "var(--muted-light)" }}>—</span>}
                            </div>
                        </div>

                        <Field label="Profile Completed">
                            <span className={user.profileCompleted ? "status-on" : "status-off"}>
                                {user.profileCompleted ? "Yes" : "No"}
                            </span>
                        </Field>
                        <Field label="Access Scope">{user.accessScope || "—"}</Field>
                        {/* <Field label="Access IDs">
                            {user.accessIds?.length > 0 ? user.accessIds.join(", ") : "—"}
                        </Field> */}

                        {user.accessNames && (
                            <>
                                {/* {renderList("Zones", user.accessNames.zones)} */}
                                {renderList("States", user.accessNames.states)}
                                {renderList("Senatorial Districts", user.accessNames.senatorialDistricts)}
                                {renderList("LGAs", user.accessNames.lgas)}
                                {renderList("Wards", user.accessNames.wards)}
                            </>
                        )}

                        <Field label="Created At">{new Date(user.createdAt).toLocaleString()}</Field>
                        <Field label="Last Updated">{new Date(user.updatedAt).toLocaleString()}</Field>
                        <Field label="Last Login">
                            {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : "—"}
                        </Field>
                    </div>

                    {/* Actions */}
                    <div className="adm-detail-actions">
                        <button className="adm-btn adm-btn-secondary" onClick={() => navigate(-1)}>
                            ← Back
                        </button>
                        <Link className="adm-btn adm-btn-primary"
                            to="/admin/users/edit" state={{ userId: user._id }}>
                            ✏ Edit User
                        </Link>
                        {!user.emailVerified && (
                            <button className="adm-btn adm-btn-blue"
                                onClick={() => { const t = genToken(); setResendUserId(user._id); setResendToken(t); setResendOpen(true); }}>
                                ✉ Resend Invite
                            </button>
                        )}
                        <button className="adm-btn adm-btn-danger"
                            onClick={() => { const t = genToken(); setForceLogoutUserId(user._id); setForceLogoutToken(t); setForceLogoutOpen(true); }}>
                            ⏏ Force Logout
                        </button>
                    </div>
                </div>
            </div>

            <ConfirmDialog show={resendOpen} token={resendToken}
                action="You are about to resend the registration email"
                onConfirm={handleConfirmResend} onCancel={() => { setResendOpen(false); setResendUserId(null); }} />

            <ConfirmDialog show={forceLogoutOpen} token={forceLogoutToken}
                action="You are about to force logout this user"
                onConfirm={handleConfirmForceLogout} onCancel={() => { setForceLogoutOpen(false); setForceLogoutUserId(null); }} />
        </div>
    );
};

export default AdminUser;